import React, { useEffect, useState } from "react";
import "./QuestionWisePage.css";
import QuestionGridNumber from "../../components/QuestionWise/QuestionGridNumber";
import QuestionText from "../../components/QuestionWise/QuestionText";
import useAppHooks from "../../../../shared/hooks/useAppHooks";

import {
  fetchPracticeSolutionRequest,
  fetchQuestionAnalyticsRequest,
} from "../../../../redux/exam_analytics/analyticsSlice";

import Modal from "../../../../shared/components/Modal/Modal";
import { ClipLoader } from "react-spinners";
import ScoreBar from "../../../../shared/components/ScoreBar/ScoreBar";

export default function QuestionWisePage({
  showQuestionGrid = true,
  showBottomAnalytics = true,
}) {
  const { dispatch, selector } = useAppHooks();

  /* ---------------- REDUX STATE ---------------- */
  const { practiceSolution, questionAnalytics, loading, error } = selector(
    (s) => s.analyticsSlice
  );

  const { paperId, testId, testType, testDetails } = selector(
    (s) => s.examSlice
  );

  /* ---------------- LOCAL STATE (KEY FIX) ---------------- */
  const [selectedQuestionAnalytics, setSelectedQuestionAnalytics] =
    useState(null);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  /* ---------------- DERIVED VALUES ---------------- */
  const finalPaperId = paperId || testDetails?.paper_id;
  const finalSetId = testDetails?.setId || testDetails?.set_id;
  const finalPaperType = testType?.id;
  const finalTestId =
    testId ||
    testDetails?.testId ||
    testDetails?.setId ||
    testDetails?.set_id;

  const practiceLoading = loading?.practice || false;
  const practiceError = error?.practice || null;
  const qLoading = loading?.question || false;

  const data = practiceSolution?.data || [];

  /* ---------------- PRACTICE SOLUTION ---------------- */
  useEffect(() => {
    // if (finalPaperId && finalPaperType && finalTestId) {
    if ((finalPaperId || finalSetId) && finalPaperType && finalTestId){
      dispatch(
        fetchPracticeSolutionRequest({
          paperId: finalPaperId,
          testId: finalTestId,
            setId: finalSetId,
          paperType: finalPaperType,
        })
      );
    }
  }, [dispatch, finalPaperId,finalSetId, finalPaperType, finalTestId]);

  /* ---------------- SYNC QUESTION ANALYTICS → LOCAL STATE ---------------- */
  useEffect(() => {
    if (questionAnalytics?.data && isAnalyticsOpen) {
      setSelectedQuestionAnalytics(questionAnalytics.data);
    }
  }, [questionAnalytics, isAnalyticsOpen]);

  /* ---------------- HANDLERS ---------------- */
  const handleAnalysis = () => {
    const qid = data[selectedIndex]?.qid;
    const questionPaperId = data[selectedIndex]?.paper_id;
    if (!qid) return;

    setSelectedQuestionAnalytics(null); // reset previous data
    setIsAnalyticsOpen(true);

    dispatch(
      fetchQuestionAnalyticsRequest({
        paperId: questionPaperId || finalPaperId,
        setId: finalSetId,
        paperType: finalPaperType,
        qid,
      })
    );
  };

  const closeAnalyticsModal = () => {
    setIsAnalyticsOpen(false);
    setSelectedQuestionAnalytics(null);
  };

  const handleNext = () => {
    setSelectedIndex((prev) =>
      prev < data.length - 1 ? prev + 1 : prev
    );
  };

  const handlePrev = () => {
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  /* ---------------- HELPERS ---------------- */
  const formatTime = (value) => {
    if (value === null || value === undefined) return "--";
    const sec = parseInt(value, 10);
    if (isNaN(sec)) return "--";
    return `${Math.floor(sec / 60)}:${(sec % 60)
      .toString()
      .padStart(2, "0")}`;
  };

  const getPercent = (val, total) =>
    !total ? 0 : (val / total) * 100;

  const getAnswerStatus = (ans) => {
    if (ans === "RIGHT") return "Correct";
    if (ans === "WRONG") return "Incorrect";
    return "Unanswered";
  };

  const currentQuestion = data[selectedIndex] || {};
  const currentTopic =
    currentQuestion?.topic_name || currentQuestion?.topic || "--";
  const currentType = currentQuestion?.type || "--";
  const currentPosition = selectedIndex + 1;
  const totalQuestions =
    Number(currentQuestion?.total_questions) || data.length;

  /* ---------------- STATES ---------------- */
  if (practiceLoading) {
    return (
      <div className="questionPage_loading">
        <ClipLoader color="#C61C26" size={50} />
      </div>
    );
  }

  if (practiceError) {
    return (
      <div className="questionPage_error">
        <p>Error: {practiceError}</p>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="questionPage_empty">
        <p>No question data available.</p>
      </div>
    );
  }

  /* ---------------- UI ---------------- */
  return (
    <div
      className={`questionPage_wrapper ${
        !showQuestionGrid ? "questionPage_wrapper--no-grid" : ""
      }`}
    >
      <div className="questionPage_meta">
        <div className="questionPage_progress">
          {currentPosition} / {totalQuestions}
        </div>

        <div className="questionPage_info_row">
        <div className="questionPage_info_item">
            <span className="questionPage_info_label">Topic:</span>
            <span className="questionPage_info_value">{currentTopic}</span>
          </div>
          <div className="questionPage_info_item">
            <span className="questionPage_info_label">Question Type:</span>
            <span className="questionPage_info_value">{currentType}</span>
          </div>
        </div>

        <div className="questionPage_top_nav">
          <button
            className="questionPage_circle_btn"
            onClick={handlePrev}
            disabled={selectedIndex === 0}
            aria-label="Previous question"
          >
            {"<"}
          </button>
          <span className="questionPage_question_label">
            Question {currentPosition}
          </span>
          <button
            className="questionPage_circle_btn"
            onClick={handleNext}
            disabled={selectedIndex === data.length - 1}
            aria-label="Next question"
          >
            {">"}
          </button>
        </div>
      </div>

      <div className="questionPage_left">
        <QuestionText data={data[selectedIndex]} />
      </div>

      {showQuestionGrid && (
        <div className="questionPage_right">
          <QuestionGridNumber
            selectedIndex={selectedIndex}
            onSelect={setSelectedIndex}
          />
        </div>
      )}

      <div className="bottom_nav_buttons">
        <div className="nav_btn_group">
          <button
            className="nav_btn"
            onClick={handlePrev}
            disabled={selectedIndex === 0}
          >
            Previous
          </button>
          <button
            className="nav_btn"
            onClick={handleNext}
            disabled={selectedIndex === data.length - 1}
          >
            Next
          </button>
        </div>

        {showBottomAnalytics && (
          <button className="nav_btn analysis_btn" onClick={handleAnalysis}>
            Analytics
          </button>
        )}
      </div>

      {/* ---------------- MODAL ---------------- */}
      <Modal
        isOpen={isAnalyticsOpen}
        onClose={closeAnalyticsModal}
        width="525px"
        height="625px"
        backgroundColor="#fff"
        borderRadius="1rem"
      >
        {qLoading || !selectedQuestionAnalytics ? (
          <div className="questionPage_loading">
            <ClipLoader color="#0066FF" size={45} />
          </div>
        ) : (
          <div className="qa_modal_wrapper">
            <h2 className="qa_title">Question Analysis</h2>

            <div className="qa_header_block">
              <div className="qa_header_text">
                <p className="qa_subject">
                  {selectedQuestionAnalytics.subject}
                </p>
                <p className="qa_topic">
                  {selectedQuestionAnalytics.topic}
                </p>
              </div>

              <div className="qa_tags">
                <span className="qa_tag_easy">
                  {selectedQuestionAnalytics.toughness}
                </span>
                <span className="qa_tag_correct">
                  {getAnswerStatus(
                    selectedQuestionAnalytics.your_answer
                  )}
                </span>
              </div>
            </div>

            <h3 className="qa_stats_heading">
              Student Performance Statistics
            </h3>

            <div className="qa_stats_box">
              <div className="qa_stat_column">
                <ScoreBar
                  label="Correct"
                  score={getPercent(
                    selectedQuestionAnalytics.correct_students,
                    selectedQuestionAnalytics.total_students
                  )}
                  color="#16A34A"
                />
                <p>
                  {selectedQuestionAnalytics.correct_students} Students
                </p>
              </div>

              <div className="qa_stat_column">
                <ScoreBar
                  label="Incorrect"
                  score={getPercent(
                    selectedQuestionAnalytics.incorrect_students,
                    selectedQuestionAnalytics.total_students
                  )}
                  color="#E33629"
                />
                <p>
                  {selectedQuestionAnalytics.incorrect_students} Students
                </p>
              </div>

              <div className="qa_stat_column">
                <ScoreBar
                  label="Unanswered"
                  score={getPercent(
                    selectedQuestionAnalytics.unanswered_students,
                    selectedQuestionAnalytics.total_students
                  )}
                  color="#80848C"
                />
                <p>
                  {selectedQuestionAnalytics.unanswered_students} Students
                </p>
              </div>
            </div>

            <div className="qa_time_row">
              <div className="qa_time_item">
                <p className="qa_time_title">Your Time</p>
                <p className="qa_time_value qa_time_blue">
                  {formatTime(selectedQuestionAnalytics.your_time)}
                </p>
              </div>

              <div className="qa_time_item">
                <p className="qa_time_title">Average Time</p>
                <p className="qa_time_value qa_time_black">
                  {formatTime(
                    selectedQuestionAnalytics.average_time
                  )}
                </p>
              </div>

              <div className="qa_time_item">
                <p className="qa_time_title">Topper Time</p>
                <p className="qa_time_value qa_time_green">
                  {formatTime(
                    selectedQuestionAnalytics.topper_time
                  )}
                </p>
              </div>
            </div>

            <button
              className="qa_close_btn"
              onClick={closeAnalyticsModal}
            >
              Close
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
